import type { OutreachInviter } from '@/api/types';

/** An outreacher's contact details: what PATCH …/contact reads and writes. */
export type ContactKey = 'contactWhatsapp' | 'contactDiscord' | 'contactTelegram' | 'contactOther';
export type ContactDetails = Record<ContactKey, string>;
export type ContactPatch = Partial<Record<ContactKey, string | null>>;

export const CONTACT_FIELDS: readonly {
  key: ContactKey;
  label: string;
  placeholder: string;
  hint?: string;
  multiline?: boolean;
}[] = [
  {
    key: 'contactWhatsapp',
    label: 'WhatsApp number',
    placeholder: '+44 7700 900123',
    hint: 'With country code.',
  },
  { key: 'contactDiscord', label: 'Discord', placeholder: 'username or user ID' },
  { key: 'contactTelegram', label: 'Telegram', placeholder: '@username' },
  {
    key: 'contactOther',
    label: 'Anything else',
    placeholder: 'Another number, best hours to reach you, time zone…',
    multiline: true,
  },
];

export function contactDetailsOf(inviter: Partial<Pick<OutreachInviter, ContactKey>>): ContactDetails {
  return {
    contactWhatsapp: inviter.contactWhatsapp ?? '',
    contactDiscord: inviter.contactDiscord ?? '',
    contactTelegram: inviter.contactTelegram ?? '',
    contactOther: inviter.contactOther ?? '',
  };
}

export const hasContactDetails = (inviter: Partial<Pick<OutreachInviter, ContactKey>>): boolean =>
  Object.values(contactDetailsOf(inviter)).some((v) => v.trim() !== '');

/**
 * Digits with an optional leading +, the way the backend stores a number.
 * Drops spacing, brackets, dashes and invisible formatting characters: a
 * number copied out of WhatsApp or Contacts comes wrapped in direction marks.
 */
export const normalizeWhatsapp = (raw: string): string => raw.replace(/[\s\p{Cf}().-]/gu, '');

/** Error text for a WhatsApp number, or null when it is empty or valid. */
export function whatsappError(raw: string): string | null {
  const v = normalizeWhatsapp(raw);
  if (v === '' || /^\+?\d{6,15}$/.test(v)) return null;
  return 'Enter a phone number with country code, e.g. +44 7700 900123';
}

/** wa.me chat link for a stored number, or null when there isn't one. */
export function whatsappHref(raw: string | null | undefined): string | null {
  const digits = normalizeWhatsapp(raw ?? '').replace(/^\+/, '');
  return /^\d{6,15}$/.test(digits) ? `https://wa.me/${digits}` : null;
}

/** Only the fields that changed; a cleared field is sent as null. */
export function contactPatch(before: ContactDetails, after: ContactDetails): ContactPatch {
  const patch: ContactPatch = {};
  for (const { key } of CONTACT_FIELDS) {
    // The number goes out the way it is stored, so a paste with stray
    // characters saves cleanly and doesn't read as a change next time.
    const clean = (v: string): string => (key === 'contactWhatsapp' ? normalizeWhatsapp(v) : v.trim());
    const next = clean(after[key]);
    if (next !== clean(before[key])) patch[key] = next === '' ? null : next;
  }
  return patch;
}
